/*
 * Santana Supplies theme — behaviour ported from the Next.js storefront's React components.
 * Plain JS, no dependencies. Each block names the component it mirrors.
 */
(() => {
  'use strict';

  const T = window.theme || {};
  const $ = (sel, root = document) => root.querySelector(sel);
  const $$ = (sel, root = document) => [...root.querySelectorAll(sel)];
  const reduced = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const usd = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });
  const money = (cents) => usd.format(cents / 100);
  const priceRange = (min, max) => (min === max ? money(min) : `From ${money(min)}`);
  const esc = (s) =>
    String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
  const sized = (url, w) => (url ? `${url}${url.includes('?') ? '&' : '?'}width=${w}` : '');
  const icon = (name, size) => {
    const tpl = document.getElementById('tpl-icons');
    const el = tpl && tpl.content.querySelector(`[data-i="${name}"] svg`);
    if (!el) return '';
    const svg = el.cloneNode(true);
    if (size) {
      svg.setAttribute('width', size);
      svg.setAttribute('height', size);
    }
    return svg.outerHTML;
  };
  const MAX_QTY = 99;

  /* ---------- overlays: presence, scroll lock, focus trap (src/lib/hooks.ts) ---------- */

  function show(el) {
    if (!el) return;
    el.hidden = false;
    requestAnimationFrame(() => requestAnimationFrame(() => (el.dataset.state = 'open')));
  }
  function hide(el, exitMs = 480) {
    if (!el) return;
    el.dataset.state = 'closed';
    clearTimeout(el._hideTimer);
    el._hideTimer = setTimeout(() => {
      if (el.dataset.state === 'closed') el.hidden = true;
    }, reduced() ? 0 : exitMs);
  }

  let lockCount = 0;
  function lockScroll(on) {
    const el = document.documentElement;
    if (on) {
      if (lockCount++ === 0) {
        const gap = window.innerWidth - el.clientWidth;
        el.style.overflow = 'hidden';
        if (gap > 0) el.style.paddingRight = `${gap}px`;
      }
    } else if (lockCount > 0 && --lockCount === 0) {
      el.style.overflow = '';
      el.style.paddingRight = '';
    }
  }

  const FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  /** Traps Tab inside `node`, closes on Escape, restores focus on release. Returns release(). */
  function trapFocus(node, onEscape, initial) {
    const opener = document.activeElement;
    requestAnimationFrame(() => {
      const target = initial || node.querySelector(FOCUSABLE) || node;
      target.focus({ preventScroll: true });
    });
    const onKey = (e) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onEscape();
        return;
      }
      if (e.key !== 'Tab') return;
      const items = $$(FOCUSABLE, node).filter((el) => el.offsetParent !== null || el === document.activeElement);
      if (!items.length) return e.preventDefault();
      const first = items[0];
      const last = items[items.length - 1];
      const outside = !node.contains(document.activeElement);
      if (e.shiftKey && (document.activeElement === first || outside)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && (document.activeElement === last || outside)) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKey, true);
    return () => {
      document.removeEventListener('keydown', onKey, true);
      if (opener && document.contains(opener)) opener.focus({ preventScroll: true });
    };
  }

  /** A drawer/dialog with its overlay. */
  function makeOverlay({ panel, overlay, initial, exitMs, onOpen, onClose }) {
    let release = null;
    const api = {
      isOpen: false,
      open() {
        if (api.isOpen || !panel) return;
        api.isOpen = true;
        show(overlay);
        show(panel);
        lockScroll(true);
        release = trapFocus(panel, api.close, typeof initial === 'function' ? initial() : initial);
        onOpen && onOpen();
      },
      close() {
        if (!api.isOpen) return;
        api.isOpen = false;
        hide(overlay, exitMs);
        hide(panel, exitMs);
        lockScroll(false);
        release && release();
        release = null;
        onClose && onClose();
      },
    };
    overlay && overlay.addEventListener('click', api.close);
    return api;
  }

  /* ---------- quantity stepper (QtyStepper) ---------- */

  function bindQty(root, onChange) {
    const input = $('[data-qty-input]', root);
    const minus = $('[data-qty-minus]', root);
    const plus = $('[data-qty-plus]', root);
    const get = () => Math.min(MAX_QTY, Math.max(1, parseInt(input.value, 10) || 1));
    const set = (n) => {
      n = Math.min(MAX_QTY, Math.max(1, n));
      input.value = n;
      minus.disabled = n <= 1;
      plus.disabled = n >= MAX_QTY;
      onChange && onChange(n);
    };
    minus.addEventListener('click', () => set(get() - 1));
    plus.addEventListener('click', () => set(get() + 1));
    input.addEventListener('change', () => set(get()));
    return { get, set };
  }

  /* ---------- cart (CartContext / CartDrawer / CartLines / CartPageView) ---------- */

  const Cart = {
    data: null,
    queue: Promise.resolve(),
    listeners: [],

    async request(url, body) {
      const res = await fetch(url, {
        method: body ? 'POST' : 'GET',
        headers: body ? { 'Content-Type': 'application/json', Accept: 'application/json' } : { Accept: 'application/json' },
        body: body ? JSON.stringify(body) : undefined,
      });
      const json = await res.json().catch(() => null);
      if (!res.ok) throw new Error((json && (json.description || json.message)) || 'Cart request failed');
      return json;
    },
    run(fn) {
      this.queue = this.queue.then(fn).catch((e) => {
        console.warn('[cart]', e.message);
        toast(e.message);
      });
      return this.queue;
    },
    async load() {
      this.data = await this.request(`${T.routes.cart}.js`);
      this.render();
    },
    add(id, quantity = 1, { openDrawer = true } = {}) {
      return this.run(async () => {
        await this.request(`${T.routes.cartAdd}.js`, { items: [{ id, quantity }] });
        this.data = await this.request(`${T.routes.cart}.js`);
        this.render();
        bumpCount();
        if (openDrawer) drawer.open();
      });
    },
    change(key, quantity) {
      return this.run(async () => {
        this.data = await this.request(`${T.routes.cartChange}.js`, { id: key, quantity });
        this.render();
      });
    },
    clear() {
      return this.run(async () => {
        this.data = await this.request(`${T.routes.cartClear}.js`, {});
        this.render();
      });
    },
    render() {
      const c = this.data;
      if (!c) return;
      // header badge
      $$('[data-cart-count]').forEach((el) => {
        el.hidden = c.item_count === 0;
        el.textContent = c.item_count > 99 ? '99+' : c.item_count;
      });
      $$('[data-open-cart]').forEach((b) => b.setAttribute('aria-label', `Cart, ${c.item_count} ${c.item_count === 1 ? 'item' : 'items'}`));

      const empty = c.item_count === 0;
      const d = $('[data-cart-drawer]');
      if (d) {
        $('[data-cart-empty]', d).hidden = !empty;
        $('[data-cart-filled]', d).hidden = empty;
      }
      const page = $('[data-cart-page]');
      if (page) {
        $('[data-cart-page-empty]', page).hidden = !empty;
        $('[data-cart-page-filled]', page).hidden = empty;
      }
      $$('[data-ship-progress]').forEach((el) => (el.innerHTML = shipProgressHtml(c.total_price)));
      $$('[data-cart-totals]').forEach((el) => (el.innerHTML = totalsHtml(c)));
      $$('[data-cart-lines]').forEach((ul) => renderLines(ul, c.items));
    },
  };

  function shipProgressHtml(subtotal) {
    const t = T.freeShippingThreshold || 25000;
    const remaining = Math.max(0, t - subtotal);
    const pct = Math.min(100, (subtotal / t) * 100);
    const text =
      remaining === 0
        ? '<span><strong>Your order qualifies for free U.S. shipping.</strong> Any requirements are confirmed at checkout.</span>'
        : `<span>Add <strong>${money(remaining)}</strong> more for free shipping on qualifying U.S. orders over ${money(t).replace('.00', '')}.</span>`;
    return `<div class="ship-progress" data-done="${remaining === 0}" aria-live="polite">${text}<div class="progress" aria-hidden="true"><span style="width: ${pct}%"></span></div></div>`;
  }

  function totalsHtml(c) {
    const n = c.item_count;
    return `<div class="totals">
        <div class="grand"><span>Subtotal</span><span class="price">${money(c.total_price)}</span></div>
        <div class="muted"><span>Shipping &amp; taxes calculated at checkout</span></div>
      </div>
      <a href="/checkout" class="btn btn--gold btn--lg btn--block" style="margin-top: 16px"${n ? '' : ' aria-disabled="true"'}>
        ${icon('lock', 18)} Checkout · ${n} ${n === 1 ? 'item' : 'items'}
      </a>
      <p class="secure-note">${icon('lock')} Secure checkout on Shopify — Shop Pay, cards &amp; more</p>`;
  }

  function renderLines(ul, items) {
    const keep = new Map($$(':scope > li[data-key]', ul).map((li) => [li.dataset.key, li]));
    const frag = document.createDocumentFragment();
    for (const it of items) {
      let li = keep.get(it.key);
      const html = lineInnerHtml(it);
      if (li) {
        keep.delete(it.key);
        if (li._html !== html) {
          li.innerHTML = html;
          li._html = html;
          bindLine(li, it);
        }
      } else {
        li = document.createElement('li');
        li.className = 'line';
        li.dataset.key = it.key;
        li.innerHTML = html;
        li._html = html;
        bindLine(li, it);
      }
      frag.appendChild(li);
    }
    keep.forEach((li) => li.remove());
    ul.appendChild(frag);
  }

  function lineInnerHtml(it) {
    const variant = it.product_has_only_default_variant ? '' : it.variant_title;
    const title = esc(it.product_title);
    return `<a href="${esc(it.url)}" class="line__img" tabindex="-1" aria-hidden="true">${
      it.image ? `<img src="${esc(sized(it.image, 168))}" alt="" width="84" height="84" loading="lazy">` : ''
    }</a>
      <div>
        <div class="line__top">
          <div>
            <a href="${esc(it.url)}" class="line__title">${title}</a>
            ${variant ? `<p class="line__variant">${esc(variant)}</p>` : ''}
          </div>
          <span class="price" style="font-size: var(--fs-sm)">${money(it.final_line_price)}</span>
        </div>
        <div class="line__bottom">
          <div class="qty qty--sm" role="group" aria-label="Quantity for ${title}" data-qty>
            <button type="button" data-qty-minus aria-label="Decrease quantity for ${title}"${it.quantity <= 1 ? ' disabled' : ''}>${icon('minus')}</button>
            <input type="number" inputmode="numeric" min="1" max="${MAX_QTY}" value="${it.quantity}" aria-label="Quantity" data-qty-input>
            <button type="button" data-qty-plus aria-label="Increase quantity for ${title}"${it.quantity >= MAX_QTY ? ' disabled' : ''}>${icon('plus')}</button>
          </div>
          <button type="button" class="line__remove" data-line-remove>Remove<span class="sr-only"> ${title}</span></button>
        </div>
      </div>`;
  }

  function bindLine(li, it) {
    bindQty($('[data-qty]', li), (n) => n !== it.quantity && Cart.change(it.key, n));
    $('[data-line-remove]', li).addEventListener('click', () => {
      li.dataset.removing = 'true';
      setTimeout(() => Cart.change(it.key, 0), 240);
    });
  }

  function bumpCount() {
    $$('[data-cart-count]').forEach((el) => {
      el.dataset.bump = 'false';
      void el.offsetWidth;
      el.dataset.bump = 'true';
      setTimeout(() => (el.dataset.bump = 'false'), 600);
    });
  }

  function toast(text) {
    const el = document.createElement('div');
    el.className = 'toast';
    el.setAttribute('role', 'status');
    el.textContent = text;
    document.body.appendChild(el);
    setTimeout(() => el.remove(), 3200);
  }

  const drawer = makeOverlay({
    panel: $('[data-cart-drawer]'),
    overlay: $('[data-cart-overlay]'),
    onOpen: () => Cart.load(),
  });
  $$('[data-open-cart]').forEach((b) => b.addEventListener('click', drawer.open));
  $$('[data-close-cart]').forEach((b) => b.addEventListener('click', drawer.close));
  $('[data-cart-clear]')?.addEventListener('click', () => Cart.clear());
  if ($('[data-cart-page]')) Cart.load();

  /* ---------- header (Header.tsx) ---------- */

  const header = $('[data-header]');
  if (header) {
    const onScroll = () => (header.dataset.scrolled = String(window.scrollY > 8));
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });

    const mega = $('[data-mega]', header);
    const megaBtn = $('[data-mega-button]', header);
    let closeTimer;
    const setMega = (open) => {
      if (!mega) return;
      clearTimeout(closeTimer);
      mega.dataset.open = String(open);
      mega.setAttribute('aria-hidden', String(!open));
      megaBtn.setAttribute('aria-expanded', String(open));
      $$('a', mega).forEach((a) => (a.tabIndex = open ? 0 : -1));
    };
    const scheduleClose = () => {
      clearTimeout(closeTimer);
      closeTimer = setTimeout(() => setMega(false), 160);
    };
    if (mega) {
      $('[data-mega-trigger]', header).addEventListener('mouseenter', () => setMega(true));
      $$('[data-mega-leave]', header).forEach((li) => li.addEventListener('mouseenter', scheduleClose));
      header.addEventListener('mouseleave', scheduleClose);
      mega.addEventListener('mouseenter', () => clearTimeout(closeTimer));
      // keyboard (detail 0) toggles; a mouse click keeps open what hover just opened
      megaBtn.addEventListener('click', (e) => setMega(e.detail === 0 ? mega.dataset.open !== 'true' : true));
      $$('a', mega).forEach((a) => a.addEventListener('click', () => setMega(false)));
    }

    // mobile menu
    const menu = makeOverlay({ panel: $('[data-menu-drawer]'), overlay: $('[data-menu-overlay]') });
    const menuBtn = $('[data-open-menu]', header);
    menuBtn?.addEventListener('click', () => {
      menu.open();
      menuBtn.setAttribute('aria-expanded', 'true');
    });
    $$('[data-close-menu]').forEach((b) =>
      b.addEventListener('click', () => {
        menu.close();
        menuBtn?.setAttribute('aria-expanded', 'false');
      }),
    );
    const shopToggle = $('[data-mnav-shop]');
    shopToggle?.addEventListener('click', () => {
      const expanded = shopToggle.getAttribute('aria-expanded') !== 'true';
      shopToggle.setAttribute('aria-expanded', String(expanded));
      $('[data-mnav-sub]').hidden = !expanded;
      const svg = $('svg', shopToggle);
      if (svg) svg.style.transform = expanded ? 'rotate(180deg)' : '';
    });

    // "/" opens search, Escape closes the mega menu
    window.addEventListener('keydown', (e) => {
      const el = e.target;
      if (e.key === '/' && !/INPUT|TEXTAREA|SELECT/.test(el.tagName) && !el.isContentEditable) {
        e.preventDefault();
        search.open();
      }
      if (e.key === 'Escape') setMega(false);
    });
  }

  /* ---------- search (SearchDialog.tsx + src/lib/search.ts) ---------- */

  const norm = (s) =>
    s
      .toLowerCase()
      .normalize('NFKD')
      .replace(/[̀-ͯ]/g, '')
      .replace(/[®™]/g, '')
      .replace(/[^a-z0-9+]+/g, ' ')
      .trim();

  function searchDocs(docs, query) {
    const tokens = norm(query).split(' ').filter(Boolean);
    if (!tokens.length) return [];
    const scored = [];
    for (const d of docs) {
      const title = norm(d.title);
      const brand = norm(d.brand || '');
      const type = norm(d.productType || '');
      const sku = norm(d.skus.join(' '));
      const rest = norm(d.tags.join(' ') + ' ' + d.collections.join(' '));
      let s = 0;
      let all = true;
      for (const t of tokens) {
        let hit = 0;
        if (new RegExp(`(^| )${t.replace(/\+/g, '\\+')}`).test(title)) hit = 6;
        else if (title.includes(t)) hit = 4;
        if (brand.startsWith(t)) hit = Math.max(hit, 5);
        if (sku.includes(t)) hit = Math.max(hit, 5);
        if (type.includes(t)) hit = Math.max(hit, 3);
        if (!hit && rest.includes(t)) hit = 2;
        if (!hit) {
          all = false;
          break;
        }
        s += hit;
      }
      if (all) scored.push({ d, s: s + (d.available ? 0.5 : 0) });
    }
    return scored.sort((a, b) => b.s - a.s || a.d.title.localeCompare(b.d.title)).map((x) => x.d);
  }

  let indexPromise = null;
  function loadSearchIndex() {
    if (!indexPromise)
      indexPromise = fetch(`${T.routes.allProducts}?view=search-index`)
        .then((r) => {
          if (!r.ok) throw new Error('index');
          return r.json();
        })
        .catch((e) => {
          indexPromise = null;
          throw e;
        });
    return indexPromise;
  }

  const searchSubmitUrl = (q) => `${T.routes.search}?type=product&q=${encodeURIComponent(q)}`;
  const dialog = $('[data-search-dialog]');
  const searchInput = $('[data-search-input]');
  const search = makeOverlay({
    panel: dialog,
    overlay: $('[data-search-overlay]'),
    initial: searchInput,
    exitMs: 400,
    onOpen: () => {
      searchInput.focus();
      loadSearchIndex()
        .then((docs) => {
          searchState.docs = docs;
          renderSearch();
        })
        .catch(() => {
          searchState.error = true;
          renderSearch();
        });
    },
  });
  const searchState = { docs: null, error: false, active: -1, shown: [], total: 0 };

  function renderSearch() {
    if (!dialog) return;
    const q = searchInput.value;
    const out = $('[data-search-output]', dialog);
    $('[data-search-suggestions]', dialog).hidden = Boolean(q.trim());
    const { docs, error } = searchState;
    const results = docs && q.trim() ? searchDocs(docs, q) : [];
    searchState.shown = results.slice(0, 6);
    searchState.total = results.length;
    searchInput.setAttribute('aria-expanded', String(searchState.shown.length > 0));
    let html = '';
    if (error) html = '<p class="muted">Search is unavailable right now. Please try again.</p>';
    else if (q.trim() && !docs)
      html = `<div style="display: grid; gap: 8px">${'<div class="skeleton" style="height: 76px; border-radius: 10px"></div>'.repeat(3)}</div>`;
    else if (q.trim() && !results.length)
      html = `<p class="muted" style="padding: 12px 0">No products match “${esc(q)}”. Try a brand (e.g. StyleCraft) or a product type.</p>`;
    else if (searchState.shown.length) {
      html = `<ul role="listbox" id="search-listbox" aria-label="Product suggestions" style="list-style: none; margin: 0; padding: 0">${searchState.shown
        .map(
          (d, i) => `<li role="option" id="sr-${i}" aria-selected="${i === searchState.active}">
          <a href="/products/${esc(d.handle)}" class="search-hit" aria-selected="${i === searchState.active}" tabindex="-1">
            ${d.image ? `<img src="${esc(d.image)}" alt="" width="56" height="56">` : '<span></span>'}
            <span>${d.brand ? `<small>${esc(d.brand)}</small>` : ''}${esc(d.title)}</span>
            <span class="price" style="font-size: var(--fs-sm)">${d.compareAtMax ? `<span class="price__compare">${money(d.compareAtMax)}</span>` : ''}${priceRange(d.priceMin, d.priceMax)}</span>
          </a>
        </li>`,
        )
        .join('')}</ul>
        <a href="${esc(searchSubmitUrl(q.trim()))}" class="link-arrow" style="margin: 16px 12px 0">See all ${results.length} results ${icon('arrow-right')}</a>`;
    }
    out.innerHTML = html;
    searchInput.setAttribute('aria-activedescendant', searchState.active >= 0 ? `sr-${searchState.active}` : '');
  }

  if (dialog) {
    $$('[data-open-search]').forEach((b) => b.addEventListener('click', search.open));
    $$('[data-close-search]').forEach((b) => b.addEventListener('click', search.close));
    searchInput.addEventListener('input', () => {
      searchState.active = -1;
      renderSearch();
    });
    searchInput.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        searchState.active = Math.min(searchState.shown.length - 1, searchState.active + 1);
        renderSearch();
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        searchState.active = Math.max(-1, searchState.active - 1);
        renderSearch();
      }
    });
    $('[data-search-form]', dialog).addEventListener('submit', (e) => {
      e.preventDefault();
      const q = searchInput.value.trim();
      const hit = searchState.shown[searchState.active];
      if (hit) window.location.href = `/products/${hit.handle}`;
      else if (q) window.location.href = searchSubmitUrl(q);
    });
    $$('[data-search-chip]', dialog).forEach((chip) =>
      chip.addEventListener('click', () => {
        searchInput.value = chip.textContent.trim();
        searchState.active = -1;
        renderSearch();
        searchInput.focus();
      }),
    );
  }

  // search page (SearchResults.tsx)
  const searchGrid = $('[data-search-grid]');
  if (searchGrid) {
    const q = searchGrid.dataset.q || '';
    const cards = $$(':scope > [data-card]', searchGrid);
    const byHandle = new Map(cards.map((c) => [c.dataset.handle, c]));
    cards.forEach((c) => c.remove());
    const countEl = $('[data-search-count]');
    countEl.textContent = 'Searching…';
    loadSearchIndex()
      .then((docs) => {
        const results = searchDocs(docs, q)
          .map((d) => byHandle.get(d.handle))
          .filter(Boolean);
        results.forEach((c, i) => {
          c.style.setProperty('--i', i % 12);
          searchGrid.appendChild(c);
        });
        countEl.textContent = `${results.length} ${results.length === 1 ? 'result' : 'results'} for “${q}”`;
        $('[data-search-empty]').hidden = results.length > 0;
        searchGrid.hidden = !results.length;
      })
      .catch(() => {
        countEl.textContent = 'Search is unavailable right now. Please try again.';
      });
  }

  /* ---------- product cards (ProductCard.tsx) ---------- */

  function bindCard(card) {
    if (card._bound) return;
    card._bound = true;
    const alt = $('img.card__alt', card);
    if (alt) {
      card.addEventListener('pointerenter', (e) => {
        if (e.pointerType !== 'mouse' || alt.src) return;
        alt.addEventListener('load', () => (card.dataset.alt = 'true'), { once: true });
        alt.srcset = alt.dataset.altSrcset;
        alt.src = alt.dataset.altSrc;
      });
    }
    const btn = $('[data-card-add]', card);
    if (!btn) return;
    btn.addEventListener('click', async () => {
      if (btn.disabled) return;
      if (btn.dataset.variantId) {
        await Cart.add(Number(btn.dataset.variantId), 1);
        const label = btn.getAttribute('aria-label');
        btn.dataset.state = 'added';
        btn.setAttribute('aria-label', `${card.dataset.title} added to cart`);
        $('[data-icon-plus]', btn).hidden = true;
        $('[data-icon-check]', btn).hidden = false;
        setTimeout(() => {
          delete btn.dataset.state;
          btn.setAttribute('aria-label', label);
          $('[data-icon-plus]', btn).hidden = false;
          $('[data-icon-check]', btn).hidden = true;
        }, 1600);
      } else {
        openQuickView(card.dataset.handle);
      }
    });
  }
  $$('[data-card]').forEach(bindCard);

  /* ---------- variant selection (src/lib/variants.ts) ---------- */

  function variantSelection(variants, initialId) {
    const initial = variants.find((v) => v.id === initialId) || variants.find((v) => v.available) || variants[0];
    let selected = initial ? [...initial.options] : [];
    const find = (sel) => variants.find((v) => v.options.every((o, i) => sel[i] === undefined || o === sel[i]));
    return {
      get selected() {
        return selected;
      },
      get variant() {
        return find(selected) || initial;
      },
      choose(i, value) {
        const next = [...selected];
        next[i] = value;
        if (!find(next)) {
          const fallback = variants.find((v) => v.options[i] === value && v.available) || variants.find((v) => v.options[i] === value);
          selected = fallback ? [...fallback.options] : next;
        } else selected = next;
      },
      selectById(id) {
        const v = variants.find((x) => x.id === id);
        if (v) selected = [...v.options];
      },
      valueAvailable(i, value) {
        const probe = [...selected];
        probe[i] = value;
        return variants.some((v) => v.available && v.options.every((o, j) => o === probe[j]));
      },
    };
  }

  /** Wires .swatches radiogroups (VariantPicker.tsx) to a selection; calls onChange after every choice. */
  function bindPicker(root, sel, onChange) {
    const groups = $$('[data-option-index]', root);
    const sync = () => {
      groups.forEach((g) => {
        const i = Number(g.dataset.optionIndex);
        $$('.swatch', g).forEach((b) => {
          const checked = sel.selected[i] === b.dataset.value;
          const available = sel.valueAvailable(i, b.dataset.value);
          b.setAttribute('aria-checked', String(checked));
          b.tabIndex = checked ? 0 : -1;
          b.dataset.unavailable = String(!available);
          const note = $('[data-soldout-note]', b);
          if (note) note.hidden = available;
        });
        const label = $('[data-option-label]', g.closest('.option'));
        if (label) label.textContent = sel.selected[i] ?? '';
      });
    };
    groups.forEach((g) => {
      const i = Number(g.dataset.optionIndex);
      g.addEventListener('click', (e) => {
        const b = e.target.closest('.swatch');
        if (!b) return;
        sel.choose(i, b.dataset.value);
        sync();
        onChange();
      });
      g.addEventListener('keydown', (e) => {
        if (!['ArrowRight', 'ArrowLeft', 'ArrowDown', 'ArrowUp'].includes(e.key)) return;
        e.preventDefault();
        const values = $$('.swatch', g).map((b) => b.dataset.value);
        const idx = values.indexOf(sel.selected[i]);
        const dir = e.key === 'ArrowRight' || e.key === 'ArrowDown' ? 1 : -1;
        const next = values[(idx + dir + values.length) % values.length];
        sel.choose(i, next);
        sync();
        onChange();
        $$('.swatch', g).find((b) => b.dataset.value === next)?.focus();
      });
    });
    sync();
    return sync;
  }

  function pickerHtml(options, prefix) {
    return options
      .map(
        (opt, i) => `<div class="option"><fieldset>
          <legend id="${prefix}-opt-${i}">${esc(opt.name)}: <strong data-option-label></strong></legend>
          <div class="swatches" role="radiogroup" aria-labelledby="${prefix}-opt-${i}" data-option-index="${i}">
            ${opt.values
              .map(
                (val) =>
                  `<button type="button" role="radio" class="swatch" data-value="${esc(val)}" aria-checked="false" tabindex="-1"><span>${esc(val)}</span><span class="sr-only" data-soldout-note hidden> (sold out)</span></button>`,
              )
              .join('')}
          </div></fieldset></div>`,
      )
      .join('');
  }

  const priceHtml = (price, compareAt) => {
    const onSale = compareAt && compareAt > price;
    return `<span class="price${onSale ? ' price--sale' : ''}">${onSale ? '<span class="sr-only">Sale price</span>' : ''}<span class="price__now">${money(price)}</span>${
      onSale ? `<span class="sr-only">Regular price</span><s class="price__compare">${money(compareAt)}</s>` : ''
    }</span>`;
  };

  /* ---------- quick view (QuickView.tsx) ---------- */

  async function openQuickView(handle) {
    let p;
    try {
      p = await fetch(`/products/${encodeURIComponent(handle)}.js`).then((r) => r.json());
    } catch {
      window.location.href = `/products/${handle}`;
      return;
    }
    const variants = p.variants.map((v) => ({
      id: v.id,
      title: v.title,
      options: v.options,
      price: v.price,
      compareAtPrice: v.compare_at_price > v.price ? v.compare_at_price : null,
      available: v.available,
      imageId: v.featured_image ? v.featured_image.id : null,
      image: v.featured_image ? v.featured_image.src : null,
    }));
    const options = p.options.map((o) => ({ name: o.name, values: o.values }));
    const sel = variantSelection(variants);
    const brandEl = document.querySelector(`[data-card][data-handle="${CSS.escape(handle)}"]`);
    const brand = brandEl ? brandEl.dataset.brand : '';

    const overlay = document.createElement('div');
    overlay.className = 'overlay';
    overlay.style.zIndex = '105';
    overlay.hidden = true;
    overlay.setAttribute('aria-hidden', 'true');
    const modal = document.createElement('div');
    modal.className = 'modal';
    modal.hidden = true;
    modal.setAttribute('role', 'dialog');
    modal.setAttribute('aria-modal', 'true');
    modal.setAttribute('aria-labelledby', `qv-${handle}`);
    modal.innerHTML = `<button type="button" class="icon-btn modal__close" data-qv-close aria-label="Close quick view">${icon('close')}</button>
      <div class="qv">
        <div class="qv__media" data-qv-media></div>
        <div style="padding-top: 8px">
          ${brand ? `<p class="pdp__brand">${esc(brand)}</p>` : ''}
          <h2 id="qv-${esc(handle)}" style="font-size: var(--fs-lg); margin-top: 8px; line-height: 1.25; font-weight: 600; padding-right: 32px">${esc(p.title)}</h2>
          <p style="margin-top: 12px; font-size: var(--fs-md)" data-qv-price></p>
          <div class="pdp__divider"></div>
          <div data-qv-picker>${pickerHtml(options, `qv-${esc(handle)}`)}</div>
          <div class="buy-row">
            <div class="qty" role="group" aria-label="Quantity for ${esc(p.title)}" data-qty>
              <button type="button" data-qty-minus aria-label="Decrease quantity for ${esc(p.title)}" disabled>${icon('minus')}</button>
              <input type="number" inputmode="numeric" min="1" max="${MAX_QTY}" value="1" aria-label="Quantity" data-qty-input>
              <button type="button" data-qty-plus aria-label="Increase quantity for ${esc(p.title)}">${icon('plus')}</button>
            </div>
            <button type="button" class="btn btn--gold btn--lg" data-qv-add>Add to cart</button>
          </div>
          <a href="/products/${esc(handle)}" class="link-arrow" style="margin-top: 20px">View full details ${icon('arrow-right')}</a>
        </div>
      </div>`;
    document.body.append(overlay, modal);
    const qty = bindQty($('[data-qty]', modal));
    const addBtn = $('[data-qv-add]', modal);
    let lastImg = null;
    const update = () => {
      const v = sel.variant;
      $('[data-qv-price]', modal).innerHTML = v ? priceHtml(v.price, v.compareAtPrice) : '';
      addBtn.disabled = !v || !v.available;
      addBtn.textContent = v && v.available ? 'Add to cart' : 'Sold out';
      const src = (v && v.image) || p.featured_image;
      if (src && src !== lastImg) {
        lastImg = src;
        $('[data-qv-media]', modal).innerHTML = `<img src="${esc(sized(src.startsWith('//') ? 'https:' + src : src, 840))}" alt="" style="animation: enter 360ms var(--ease-out)">`;
      }
    };
    bindPicker($('[data-qv-picker]', modal), sel, update);
    update();
    const qv = makeOverlay({
      panel: modal,
      overlay,
      onClose: () => setTimeout(() => (overlay.remove(), modal.remove()), reduced() ? 0 : 500),
    });
    $('[data-qv-close]', modal).addEventListener('click', qv.close);
    addBtn.addEventListener('click', async () => {
      const v = sel.variant;
      if (!v) return;
      qv.close();
      await Cart.add(v.id, qty.get());
    });
    qv.open();
  }

  /* ---------- product rails (ProductRail.tsx) ---------- */

  $$('[data-rail-wrap]').forEach((wrap) => {
    const rail = $('[data-rail]', wrap);
    const go = (dir) => rail.scrollBy({ left: dir * rail.clientWidth * 0.85, behavior: 'smooth' });
    $('[data-rail-prev]', wrap)?.addEventListener('click', () => go(-1));
    $('[data-rail-next]', wrap)?.addEventListener('click', () => go(1));
  });

  /* ---------- product page (ProductView.tsx, ProductGallery.tsx, Lightbox.tsx) ---------- */

  const pdp = $('[data-product]');
  if (pdp) {
    const data = JSON.parse($('[data-product-json]', pdp).textContent);
    const images = data.images;
    const gallery = galleryInit(pdp, images, data.title);
    const sel = variantSelection(data.variants, data.selectedId);
    const urlId = Number(new URLSearchParams(window.location.search).get('variant'));
    if (urlId) sel.selectById(urlId);

    const priceEl = $('[data-pdp-price]', pdp);
    const addBtn = $('[data-add-to-cart]', pdp);
    const buyNow = $('[data-buy-now]', pdp);
    const stock = $('[data-stock]', pdp);
    const sticky = $('[data-sticky-buy]', pdp);
    const stickyBtn = $('[data-sticky-add]', pdp);
    const stickyPrice = $('[data-sticky-price]', pdp);
    let added = false;
    let addedTimer;

    const qtyRoot = $('[data-buy] [data-qty]', pdp);
    const qty = bindQty(qtyRoot, () => update());

    function update() {
      const v = sel.variant;
      if (!v) return;
      const save = v.compareAtPrice && v.compareAtPrice > v.price ? `Save ${money(v.compareAtPrice - v.price).replace('.00', '')}` : null;
      priceEl.innerHTML = `${priceHtml(v.price, v.compareAtPrice)}${save ? `<span class="badge badge--sale">${save}</span>` : ''}${
        v.available ? '' : '<span class="badge">Sold out</span>'
      }`;
      addBtn.disabled = !v.available;
      addBtn.innerHTML = !v.available ? 'Sold out' : added ? `${icon('check', 18)} Added to cart` : `Add to cart · ${money(v.price * qty.get())}`;
      buyNow.hidden = !v.available;
      buyNow.href = `/cart/${v.id}:${qty.get()}`;
      stock.dataset.in = String(v.available);
      stock.textContent = v.available ? 'In stock' : 'Currently sold out';
      stickyPrice.innerHTML = priceHtml(v.price, v.compareAtPrice);
      stickyBtn.disabled = !v.available;
      stickyBtn.textContent = v.available ? (added ? 'Added' : 'Add to cart') : 'Sold out';
      if (v.imageId) gallery?.showImageId(v.imageId);
      if (data.variants.length > 1) {
        const url = new URL(window.location.href);
        if (url.searchParams.get('variant') !== String(v.id)) {
          url.searchParams.set('variant', String(v.id));
          window.history.replaceState(window.history.state, '', url);
        }
      }
    }
    const picker = $('[data-variant-picker]', pdp);
    if (picker) bindPicker(picker, sel, update);
    update();

    const addToCart = async () => {
      const v = sel.variant;
      if (!v || !v.available) return;
      await Cart.add(v.id, qty.get());
      added = true;
      update();
      clearTimeout(addedTimer);
      addedTimer = setTimeout(() => {
        added = false;
        update();
      }, 2000);
    };
    addBtn.addEventListener('click', addToCart);
    stickyBtn.addEventListener('click', addToCart);

    // mobile sticky bar once the main buy buttons have scrolled away
    const buy = $('[data-buy]', pdp);
    let frame = 0;
    const check = () => {
      frame = 0;
      const visible = buy.getBoundingClientRect().bottom < 0;
      sticky.dataset.visible = String(visible);
      sticky.setAttribute('aria-hidden', String(!visible));
      stickyBtn.tabIndex = visible ? 0 : -1;
    };
    const onScroll = () => {
      if (!frame) frame = requestAnimationFrame(check);
    };
    check();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
  }

  function galleryInit(root, images, title) {
    const g = $('[data-gallery]', root);
    if (!g) return null;
    const stage = $('[data-stage]', g);
    const slides = $$('[data-slide]', g);
    const thumbs = $$('[data-thumb]', g);
    const dots = $$('[data-dot]', g);
    const blur = $('[data-stage-blur]', g);
    const openBtn = $('[data-stage-open]', g);
    const countEl = $('[data-stage-count]', g);
    const count = slides.length;
    let index = 0;
    let firstRun = true;

    const load = (i) => {
      const img = $('img', slides[i]);
      if (img && img.dataset.src && !img.getAttribute('src')) {
        img.srcset = img.dataset.srcset;
        img.src = img.dataset.src;
      }
    };
    const preloadNeighbours = () => {
      [index, (index + 1) % count, (index - 1 + count) % count].forEach(load);
    };
    // don't compete with the main image for bandwidth on first load
    setTimeout(() => {
      firstRun = false;
      preloadNeighbours();
    }, 1500);

    const set = (i) => {
      index = (i + count) % count;
      if (!firstRun) preloadNeighbours();
      else load(index);
      slides.forEach((s, j) => {
        s.dataset.active = String(j === index);
        if (j === index) s.removeAttribute('aria-hidden');
        else s.setAttribute('aria-hidden', 'true');
      });
      thumbs.forEach((t, j) => t.setAttribute('aria-current', String(j === index)));
      dots.forEach((d, j) => (d.dataset.active = String(j === index)));
      if (blur && images[index]) blur.src = sized(images[index].src.split('?')[0], 64);
      if (countEl) countEl.textContent = count > 1 ? `${index + 1}/${count} · ` : '';
      openBtn.setAttribute('aria-label', `Open image ${index + 1} of ${count} in full-screen viewer`);
      thumbs[index]?.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
    };
    const go = (d) => set(index + d);

    $('[data-stage-prev]', g)?.addEventListener('click', () => go(-1));
    $('[data-stage-next]', g)?.addEventListener('click', () => go(1));
    thumbs.forEach((t, i) => {
      t.addEventListener('click', () => set(i));
      t.addEventListener('mouseenter', () => set(i));
    });

    // hover zoom follows the pointer; swipe on touch
    let swipe = null;
    let swiped = false;
    stage.addEventListener('pointerenter', (e) => e.pointerType === 'mouse' && (stage.dataset.zoom = 'true'));
    stage.addEventListener('pointerleave', () => (stage.dataset.zoom = 'false'));
    stage.addEventListener('pointermove', (e) => {
      if (e.pointerType !== 'mouse') return;
      const r = stage.getBoundingClientRect();
      stage.style.setProperty('--zx', `${((e.clientX - r.left) / r.width) * 100}%`);
      stage.style.setProperty('--zy', `${((e.clientY - r.top) / r.height) * 100}%`);
    });
    stage.addEventListener('pointerdown', (e) => {
      swiped = false;
      if (e.pointerType !== 'mouse') swipe = { x: e.clientX, y: e.clientY, id: e.pointerId };
    });
    stage.addEventListener('pointerup', (e) => {
      const s = swipe;
      swipe = null;
      if (!s || s.id !== e.pointerId) return;
      const dx = e.clientX - s.x;
      if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(e.clientY - s.y)) {
        swiped = true;
        go(dx < 0 ? 1 : -1);
      }
    });
    openBtn.addEventListener('click', () => {
      if (swiped) return;
      stage.dataset.zoom = 'false';
      openLightbox(images, index, set, title);
    });
    openBtn.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowRight') go(1);
      else if (e.key === 'ArrowLeft') go(-1);
    });

    return {
      showImageId(id) {
        const i = images.findIndex((im) => im.id === id);
        if (i >= 0 && i !== index) set(i);
      },
    };
  }

  function openLightbox(images, startIndex, onIndex, title) {
    const MAX_SCALE = 4;
    const TAP_ZOOM = 2.5;
    const count = images.length;
    let index = startIndex;
    let view = { s: 1, x: 0, y: 0 };
    let dragY = 0;
    let dragging = false;
    const pointers = new Map();
    let gesture = null;
    let lastTap = 0;

    const lb = document.createElement('div');
    lb.className = 'lightbox';
    lb.hidden = true;
    lb.setAttribute('role', 'dialog');
    lb.setAttribute('aria-modal', 'true');
    lb.setAttribute('aria-label', `${title} — image viewer`);
    lb.innerHTML = `<div class="lightbox__bar">
        <span aria-live="polite" data-lb-count></span>
        <div>
          <button type="button" class="icon-btn" data-lb-out aria-label="Zoom out">${icon('minus')}</button>
          <button type="button" class="icon-btn" data-lb-in aria-label="Zoom in">${icon('plus')}</button>
          <button type="button" class="icon-btn" data-lb-close aria-label="Close image viewer">${icon('close')}</button>
        </div>
      </div>
      <div class="lightbox__viewport" data-lb-viewport>
        <div class="lightbox__frame" style="pointer-events: none"><img class="lightbox__img" data-lb-img alt="" draggable="false" style="pointer-events: auto"></div>
        ${
          count > 1
            ? `<button type="button" class="lightbox__nav lightbox__nav--prev" data-lb-prev aria-label="Previous image">${icon('chevron-left')}</button>
               <button type="button" class="lightbox__nav lightbox__nav--next" data-lb-next aria-label="Next image">${icon('chevron-right')}</button>`
            : ''
        }
      </div>
      ${
        count > 1
          ? `<div class="lightbox__thumbs" role="group" aria-label="Choose image">${images
              .map(
                (im, i) =>
                  `<button type="button" class="thumb" data-lb-thumb="${i}" aria-current="false" aria-label="Show image ${i + 1} of ${count}"><img src="${esc(sized(im.src.split('?')[0], 120))}" alt="" width="120" height="120"></button>`,
              )
              .join('')}</div>`
          : '<div></div>'
      }`;
    document.body.appendChild(lb);

    const vp = $('[data-lb-viewport]', lb);
    const img = $('[data-lb-img]', lb);
    const zoomed = () => view.s > 1.01;

    const clamp = (v) => {
      const s = Math.min(MAX_SCALE, Math.max(1, v.s));
      const maxX = Math.max(0, (img.offsetWidth * s - vp.clientWidth) / 2);
      const maxY = Math.max(0, (img.offsetHeight * s - vp.clientHeight) / 2);
      return { s, x: Math.min(maxX, Math.max(-maxX, v.x)), y: Math.min(maxY, Math.max(-maxY, v.y)) };
    };
    const zoomAt = (s1, cx, cy, from) => {
      const r = vp.getBoundingClientRect();
      const px = cx - (r.left + r.width / 2);
      const py = cy - (r.top + r.height / 2);
      const k = s1 / from.s;
      return clamp({ s: s1, x: px - (px - from.x) * k, y: py - (py - from.y) * k });
    };
    const paint = () => {
      img.style.transform = `translate3d(${view.x}px, ${view.y + dragY}px, 0) scale(${view.s})`;
      img.style.transition = dragging ? 'none' : 'transform 300ms var(--ease-out)';
      img.style.cursor = zoomed() ? (dragging ? 'grabbing' : 'grab') : 'zoom-in';
      vp.style.opacity = String(Math.max(0.35, 1 - Math.abs(dragY) / 400));
      vp.dataset.zoomed = String(zoomed());
      $('[data-lb-out]', lb).disabled = !zoomed();
      $('[data-lb-in]', lb).disabled = view.s >= MAX_SCALE;
    };
    const setImage = () => {
      const im = images[index];
      img.removeAttribute('srcset');
      img.src = sized(im.src.split('?')[0], 1600);
      img.alt = im.alt || '';
      img.width = im.width;
      img.height = im.height;
      img.style.animation = 'none';
      void img.offsetWidth;
      img.style.animation = 'lb-in 380ms var(--ease-out)';
      $('[data-lb-count]', lb).textContent = `${index + 1} / ${count}`;
      $$('[data-lb-thumb]', lb).forEach((t, i) => t.setAttribute('aria-current', String(i === index)));
      // preload neighbours so next/prev feel instant
      if (count > 1)
        [1, -1].forEach((d) => {
          const n = images[(index + d + count) % count];
          new Image().src = sized(n.src.split('?')[0], 1600);
        });
    };
    const go = (d) => {
      if (count < 2) return;
      view = { s: 1, x: 0, y: 0 };
      index = (index + d + count) % count;
      onIndex(index);
      setImage();
      paint();
    };
    const zoomCenter = (factor) => {
      const r = vp.getBoundingClientRect();
      view = view.s * factor <= 1.01 ? { s: 1, x: 0, y: 0 } : zoomAt(view.s * factor, r.left + r.width / 2, r.top + r.height / 2, view);
      paint();
    };

    const onKey = (e) => {
      if (e.key === 'ArrowRight') go(1);
      else if (e.key === 'ArrowLeft') go(-1);
      else if (e.key === '+' || e.key === '=') zoomCenter(1.5);
      else if (e.key === '-') zoomCenter(1 / 1.5);
      else if (e.key === '0') {
        view = { s: 1, x: 0, y: 0 };
        paint();
      }
    };
    window.addEventListener('keydown', onKey);
    vp.addEventListener(
      'wheel',
      (e) => {
        e.preventDefault();
        const s1 = Math.min(MAX_SCALE, Math.max(1, view.s * Math.exp(-e.deltaY * 0.0022)));
        view = s1 <= 1.01 ? { s: 1, x: 0, y: 0 } : zoomAt(s1, e.clientX, e.clientY, view);
        paint();
      },
      { passive: false },
    );

    vp.addEventListener('pointerdown', (e) => {
      if (e.target.closest('button')) return;
      vp.setPointerCapture(e.pointerId);
      pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
      const pts = [...pointers.values()];
      if (pts.length === 2) {
        const [a, b] = pts;
        gesture = { startX: 0, startY: 0, view, dist: Math.hypot(a.x - b.x, a.y - b.y), mid: { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 }, moved: true };
      } else {
        gesture = { startX: e.clientX, startY: e.clientY, view, moved: false, onImg: e.target === img, onBackdrop: e.target === vp || e.target.classList.contains('lightbox__frame') };
      }
      dragging = true;
      paint();
    });
    vp.addEventListener('pointermove', (e) => {
      if (!pointers.has(e.pointerId) || !gesture) return;
      pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
      const pts = [...pointers.values()];
      if (pts.length === 2 && gesture.dist) {
        const [a, b] = pts;
        const s1 = Math.min(MAX_SCALE, Math.max(1, gesture.view.s * (Math.hypot(a.x - b.x, a.y - b.y) / gesture.dist)));
        view = zoomAt(s1, gesture.mid.x, gesture.mid.y, gesture.view);
        return paint();
      }
      const dx = e.clientX - gesture.startX;
      const dy = e.clientY - gesture.startY;
      if (Math.abs(dx) + Math.abs(dy) > 6) gesture.moved = true;
      if (gesture.view.s > 1.01) view = clamp({ ...gesture.view, x: gesture.view.x + dx, y: gesture.view.y + dy });
      else if (Math.abs(dy) > Math.abs(dx)) dragY = dy;
      paint();
    });
    const onUp = (e) => {
      const g = gesture;
      pointers.delete(e.pointerId);
      if (pointers.size > 0) {
        const [rest] = [...pointers.values()];
        gesture = { startX: rest.x, startY: rest.y, view, moved: true };
        return;
      }
      dragging = false;
      gesture = null;
      if (!g) return paint();
      const dx = e.clientX - g.startX;
      const dy = e.clientY - g.startY;
      if (!zoomed() && g.view.s <= 1.01) {
        if (Math.abs(dy) > 110 && Math.abs(dy) > Math.abs(dx)) return lbApi.close();
        dragY = 0;
        if (Math.abs(dx) > 60 && Math.abs(dx) > Math.abs(dy)) return go(dx < 0 ? 1 : -1);
      }
      if (!g.moved && g.onImg) {
        const now = Date.now();
        // mouse: single click toggles zoom. touch: double-tap toggles zoom.
        if (e.pointerType !== 'touch' || now - lastTap < 300) {
          view = view.s > 1.01 ? { s: 1, x: 0, y: 0 } : zoomAt(TAP_ZOOM, e.clientX, e.clientY, view);
          lastTap = 0;
        } else lastTap = now;
      } else if (!g.moved && g.onBackdrop) {
        return lbApi.close();
      }
      paint();
    };
    vp.addEventListener('pointerup', onUp);
    vp.addEventListener('pointercancel', onUp);

    $('[data-lb-out]', lb).addEventListener('click', () => zoomCenter(1 / 1.5));
    $('[data-lb-in]', lb).addEventListener('click', () => zoomCenter(1.5));
    $('[data-lb-prev]', lb)?.addEventListener('click', () => go(-1));
    $('[data-lb-next]', lb)?.addEventListener('click', () => go(1));
    $$('[data-lb-thumb]', lb).forEach((t) =>
      t.addEventListener('click', () => {
        view = { s: 1, x: 0, y: 0 };
        index = Number(t.dataset.lbThumb);
        onIndex(index);
        setImage();
        paint();
      }),
    );

    const lbApi = makeOverlay({
      panel: lb,
      exitMs: 420,
      onClose: () => {
        window.removeEventListener('keydown', onKey);
        setTimeout(() => lb.remove(), reduced() ? 0 : 440);
      },
    });
    $('[data-lb-close]', lb).addEventListener('click', lbApi.close);
    setImage();
    paint();
    lbApi.open();
  }

  /* ---------- collection filters, sort, load more (CollectionView.tsx) ---------- */

  const catalog = $('[data-catalog]');
  if (catalog) collectionInit(catalog);

  function collectionInit(root) {
    const PAGE_SIZE = 24;
    const SORTS = ['featured', 'price-asc', 'price-desc', 'title-asc', 'title-desc', 'newest'];
    const grid = $('[data-grid]', root);
    const cards = $$(':scope > [data-card]', grid).map((el, order) => ({
      el,
      order,
      title: el.dataset.title,
      brand: el.dataset.brand || null,
      type: el.dataset.type || null,
      min: Number(el.dataset.min),
      max: Number(el.dataset.max),
      compare: el.dataset.compare ? Number(el.dataset.compare) : null,
      available: el.dataset.available === 'true',
      created: Number(el.dataset.created) || 0,
    }));
    const collectionTitle = root.dataset.title;
    const panel = $('[data-filters-panel]', root);
    const body = $('[data-filters-body]', root);
    const overlay = $('[data-filters-overlay]', root);
    const sortSel = $('[data-sort]', root);
    const mq = window.matchMedia('(max-width: 1023.98px)');

    const parse = () => {
      const sp = new URLSearchParams(window.location.search);
      const num = (k) => {
        const v = sp.get(k);
        const n = v == null || v === '' ? NaN : Number(v);
        return Number.isFinite(n) && n >= 0 ? n : null;
      };
      return {
        f: { brand: sp.getAll('brand'), type: sp.getAll('type'), avail: sp.get('avail') === '1', sale: sp.get('sale') === '1', min: num('min'), max: num('max') },
        sort: SORTS.includes(sp.get('sort')) ? sp.get('sort') : 'featured',
        page: Math.max(1, Math.floor(num('page') ?? 1)),
      };
    };
    const serialize = (f, sort, page) => {
      const sp = new URLSearchParams();
      f.brand.forEach((b) => sp.append('brand', b));
      f.type.forEach((t) => sp.append('type', t));
      if (f.avail) sp.set('avail', '1');
      if (f.sale) sp.set('sale', '1');
      if (f.min != null) sp.set('min', String(f.min));
      if (f.max != null) sp.set('max', String(f.max));
      if (sort !== 'featured') sp.set('sort', sort);
      if (page > 1) sp.set('page', String(page));
      return sp.toString();
    };
    const matches = (p, f, skip) => {
      if (skip !== 'brand' && f.brand.length && !(p.brand && f.brand.includes(p.brand))) return false;
      if (skip !== 'type' && f.type.length && !(p.type && f.type.includes(p.type))) return false;
      if (skip !== 'avail' && f.avail && !p.available) return false;
      if (skip !== 'sale' && f.sale && !p.compare) return false;
      if (skip !== 'price') {
        if (f.min != null && p.max < f.min * 100) return false;
        if (f.max != null && p.min > f.max * 100) return false;
      }
      return true;
    };
    const sortList = (list, sort) => {
      const out = [...list];
      const coll = (a, b) => a.title.localeCompare(b.title, 'en', { numeric: true });
      switch (sort) {
        case 'price-asc':
          return out.sort((a, b) => a.min - b.min);
        case 'price-desc':
          return out.sort((a, b) => b.min - a.min);
        case 'title-asc':
          return out.sort(coll);
        case 'title-desc':
          return out.sort((a, b) => coll(b, a));
        case 'newest':
          return out.sort((a, b) => b.created - a.created);
        default:
          return out.sort((a, b) => a.order - b.order);
      }
    };
    const countBy = (list, key) => {
      const m = new Map();
      list.forEach((p) => {
        const k = p[key];
        if (k) m.set(k, (m.get(k) || 0) + 1);
      });
      return m;
    };

    const allBrands = [...new Set(cards.map((p) => p.brand).filter(Boolean))].sort();
    const allTypes = [...new Set(cards.map((p) => p.type).filter(Boolean))].sort();
    const anySale = cards.some((p) => p.compare);
    const prices = cards.flatMap((p) => [p.min, p.max]);
    const bounds = prices.length ? { lo: Math.floor(Math.min(...prices) / 100), hi: Math.ceil(Math.max(...prices) / 100) } : { lo: 0, hi: 0 };

    // ----- filter panel -----
    const chev = $('.select svg', root).outerHTML;
    const checkHtml = (kind, value, label) =>
      `<label class="check" data-check="${kind}" data-value="${esc(value)}"><input type="checkbox"> ${esc(label)} <span class="count">0</span></label>`;
    body.innerHTML = `
      <details class="filter-group" open>
        <summary>Availability ${chev}</summary>
        <div class="filter-options">
          ${checkHtml('avail', '1', 'In stock')}
          ${anySale ? checkHtml('sale', '1', 'On sale') : ''}
        </div>
      </details>
      <details class="filter-group" open>
        <summary>Price ${chev}</summary>
        <form data-price-form novalidate>
          <div class="price-inputs">
            <label><span class="sr-only">Minimum price in dollars</span><input class="input" inputmode="decimal" placeholder="$${bounds.lo}" data-price-min></label>
            <span class="muted">–</span>
            <label><span class="sr-only">Maximum price in dollars</span><input class="input" inputmode="decimal" placeholder="$${bounds.hi}" data-price-max></label>
          </div>
          <p class="field" style="margin-top: 8px" data-price-error hidden><span class="error" role="alert"></span></p>
          <button type="submit" class="btn btn--sm" style="margin-top: 12px; width: 100%">Apply price</button>
        </form>
      </details>
      ${
        allTypes.length > 1
          ? `<details class="filter-group" open><summary>Product type ${chev}</summary><div class="filter-options">${allTypes.map((t) => checkHtml('type', t, t)).join('')}</div></details>`
          : ''
      }
      ${
        allBrands.length > 1
          ? `<details class="filter-group" open><summary>Brand ${chev}</summary><div class="filter-options">${allBrands.map((b) => checkHtml('brand', b, b)).join('')}</div></details>`
          : ''
      }`;

    let state = parse();
    let gridKey = null;
    let renderedCount = 0;

    const update = (next = {}, nextSort = state.sort, nextPage = 1) => {
      const f = { ...state.f, ...next };
      const qs = serialize(f, nextSort, nextPage);
      window.history.replaceState(window.history.state, '', qs ? `${window.location.pathname}?${qs}` : window.location.pathname);
      state = { f, sort: nextSort, page: nextPage };
      render();
    };
    const toggle = (key, value) => {
      const cur = state.f[key];
      update({ [key]: cur.includes(value) ? cur.filter((x) => x !== value) : [...cur, value] });
    };
    const clearAll = () => update({ brand: [], type: [], avail: false, sale: false, min: null, max: null });

    $$('[data-check]', body).forEach((label) => {
      const input = $('input', label);
      input.addEventListener('change', () => {
        const kind = label.dataset.check;
        if (kind === 'avail') update({ avail: input.checked });
        else if (kind === 'sale') update({ sale: input.checked });
        else toggle(kind, label.dataset.value);
      });
    });
    const minIn = $('[data-price-min]', body);
    const maxIn = $('[data-price-max]', body);
    [minIn, maxIn].forEach((el) => el.addEventListener('input', () => (el.value = el.value.replace(/[^\d.]/g, ''))));
    $('[data-price-form]', body).addEventListener('submit', (e) => {
      e.preventDefault();
      const errEl = $('[data-price-error]', body);
      const a = minIn.value === '' ? null : Number(minIn.value);
      const b = maxIn.value === '' ? null : Number(maxIn.value);
      let err = null;
      if ((a != null && (!Number.isFinite(a) || a < 0)) || (b != null && (!Number.isFinite(b) || b < 0))) err = 'Enter a valid amount.';
      else if (a != null && b != null && a > b) err = 'Minimum must be lower than maximum.';
      errEl.hidden = !err;
      $('.error', errEl).textContent = err || '';
      if (!err) update({ min: a, max: b });
    });
    sortSel.addEventListener('change', () => update({}, sortSel.value));
    $$('[data-clear-all]', root).forEach((b) => b.addEventListener('click', clearAll));
    $('[data-load-button]', root).addEventListener('click', () => update({}, state.sort, state.page + 1));

    // mobile filter drawer
    let release = null;
    const isDrawer = () => mq.matches && panel.dataset.state === 'open';
    const setPanel = (open) => {
      panel.dataset.state = open ? 'open' : 'closed';
      $('[data-filters-open]', root).setAttribute('aria-expanded', String(open));
      applyMode();
    };
    const applyMode = () => {
      const drawerActive = isDrawer();
      overlay.hidden = !drawerActive;
      if (mq.matches) {
        panel.setAttribute('role', 'dialog');
        if (drawerActive) {
          panel.setAttribute('aria-modal', 'true');
          panel.inert = false;
        } else {
          panel.removeAttribute('aria-modal');
          panel.inert = true;
        }
      } else {
        panel.removeAttribute('role');
        panel.removeAttribute('aria-modal');
        panel.inert = false;
      }
      if (drawerActive && !release) {
        lockScroll(true);
        release = trapFocus(panel, () => setPanel(false));
      } else if (!drawerActive && release) {
        lockScroll(false);
        release();
        release = null;
      }
    };
    $('[data-filters-open]', root).addEventListener('click', () => setPanel(true));
    $$('[data-filters-close]', root).forEach((b) => b.addEventListener('click', () => setPanel(false)));
    overlay.addEventListener('click', () => setPanel(false));
    mq.addEventListener('change', applyMode);
    applyMode();

    function render() {
      const { f, sort, page } = state;
      const filtered = sortList(
        cards.filter((p) => matches(p, f)),
        sort,
      );
      const visible = filtered.slice(0, page * PAGE_SIZE);

      // grid: rebuild (replaying the entrance stagger) when filters/sort change; append on "load more"
      const key = `${sort}|${serialize(f, sort, 1)}`;
      if (key !== gridKey) {
        gridKey = key;
        grid.replaceChildren();
        renderedCount = 0;
      }
      for (let i = renderedCount; i < visible.length; i++) {
        visible[i].el.style.setProperty('--i', i % PAGE_SIZE);
        grid.appendChild(visible[i].el);
      }
      while (grid.children.length > visible.length) grid.lastElementChild.remove();
      renderedCount = visible.length;
      grid.hidden = filtered.length === 0;
      $('[data-empty]', root).hidden = filtered.length !== 0;

      // counts reflect the other active filters
      const brandCounts = countBy(cards.filter((p) => matches(p, f, 'brand')), 'brand');
      const typeCounts = countBy(cards.filter((p) => matches(p, f, 'type')), 'type');
      const inStock = cards.filter((p) => matches(p, f, 'avail') && p.available).length;
      const onSale = cards.filter((p) => matches(p, f, 'sale') && p.compare).length;
      $$('[data-check]', body).forEach((label) => {
        const kind = label.dataset.check;
        const v = label.dataset.value;
        const input = $('input', label);
        let n = 0;
        if (kind === 'avail') (n = inStock), (input.checked = f.avail);
        else if (kind === 'sale') (n = onSale), (input.checked = f.sale);
        else if (kind === 'brand') (n = brandCounts.get(v) || 0), (input.checked = f.brand.includes(v));
        else (n = typeCounts.get(v) || 0), (input.checked = f.type.includes(v));
        $('.count', label).textContent = n;
        label.dataset.empty = String(n === 0);
      });
      if (document.activeElement !== minIn) minIn.value = f.min ?? '';
      if (document.activeElement !== maxIn) maxIn.value = f.max ?? '';
      sortSel.value = sort;

      // active filter pills
      const chips = [
        ...f.brand.map((b) => ({ label: b, clear: () => toggle('brand', b) })),
        ...f.type.map((t) => ({ label: t, clear: () => toggle('type', t) })),
        ...(f.avail ? [{ label: 'In stock', clear: () => update({ avail: false }) }] : []),
        ...(f.sale ? [{ label: 'On sale', clear: () => update({ sale: false }) }] : []),
        ...(f.min != null || f.max != null
          ? [
              {
                label: `${money((f.min != null ? f.min : bounds.lo) * 100)} – ${money((f.max != null ? f.max : bounds.hi) * 100)}`,
                clear: () => update({ min: null, max: null }),
              },
            ]
          : []),
      ];
      const active = $('[data-active-filters]', root);
      active.hidden = !chips.length;
      active.replaceChildren(
        ...chips.map((c) => {
          const b = document.createElement('button');
          b.type = 'button';
          b.className = 'pill-remove';
          b.setAttribute('aria-label', `Remove filter ${c.label}`);
          b.innerHTML = `${esc(c.label)} ${icon('close')}`;
          b.addEventListener('click', c.clear);
          return b;
        }),
      );
      if (chips.length) {
        const all = document.createElement('button');
        all.type = 'button';
        all.className = 'line__remove';
        all.textContent = 'Clear all';
        all.addEventListener('click', clearAll);
        active.appendChild(all);
      }
      $$('[data-clear-all]', panel).forEach((b) => (b.disabled = !chips.length));
      $('[data-filter-label]', root).textContent = `Filter${chips.length ? ` (${chips.length})` : ''}`;
      $('[data-result-count]', root).textContent = `${filtered.length} ${filtered.length === 1 ? 'product' : 'products'}`;
      $('[data-show-results]', root).textContent = `Show ${filtered.length} ${filtered.length === 1 ? 'result' : 'results'}`;

      const more = $('[data-load-more]', root);
      more.hidden = visible.length >= filtered.length;
      $('[data-load-text]', root).textContent = `Showing ${visible.length} of ${filtered.length}`;
      $('[data-load-bar]', root).style.width = `${filtered.length ? (visible.length / filtered.length) * 100 : 0}%`;
    }
    render();
  }

  /* ---------- FAQ accordions (src/lib/faq.ts + pages/[handle]/page.tsx) ---------- */

  const faq = $('[data-faq]');
  if (faq) {
    // Shopify's raw HTML keeps editor attributes (e.g. <span style>) that the Next.js sanitizer dropped; links keep theirs
    const clean = faq.innerHTML.replace(/<(p|span|strong|b|em|ul|ol|li|h[2-6])\s[^>]*>/g, '<$1>').replace(/<(\/?)b>/g, '<$1strong>');
    const blocks = clean.match(/<(p|ul|ol|h[2-6])>[\s\S]*?<\/\1>/g);
    let intro = '';
    const sections = [];
    let cur = null;
    let q = null;
    const strip = (s) => {
      const d = document.createElement('div');
      d.innerHTML = s;
      return d.textContent.trim();
    };
    (blocks || []).forEach((b) => {
      const section = /^<p><span><strong>([\s\S]*?)<\/strong><\/span><\/p>$/.exec(b) || /^<h[23]>([\s\S]*?)<\/h[23]>$/.exec(b);
      const question = /^<p><strong>([\s\S]*?)<\/strong><\/p>$/.exec(b);
      if (section) {
        cur = { title: strip(section[1]), intro: '', items: [] };
        sections.push(cur);
        q = null;
      } else if (question && cur) {
        q = { q: strip(question[1]), a: '' };
        cur.items.push(q);
      } else if (q) q.a += b;
      else if (cur) cur.intro += b;
      else intro += b;
    });
    const restoreBullets = (html) =>
      html.replace(/<p>-\s([\s\S]*?)<\/p>/g, (_, body) => {
        const items = body
          .split(/-\s(?=[A-Z])/)
          .map((s) => s.trim())
          .filter(Boolean);
        return `<ul>${items.map((i) => `<li>${i}</li>`).join('')}</ul>`;
      });
    if (sections.length) {
      faq.classList.remove('prose');
      faq.innerHTML = `${intro ? `<div class="prose" style="margin-bottom: 40px">${intro}</div>` : ''}${sections
        .map(
          (s, si) => `<section class="faq-section" aria-labelledby="faq-${si}">
            <h2 id="faq-${si}">${esc(s.title)}</h2>
            ${s.intro ? `<div class="prose" style="margin-bottom: 12px">${s.intro}</div>` : ''}
            ${s.items
              .map(
                (it) => `<details class="acc"><summary><h3 class="acc__title">${esc(it.q)}</h3> <span class="acc__icon" aria-hidden="true"></span></summary>
                  <div class="acc__body prose">${restoreBullets(it.a)}</div></details>`,
              )
              .join('')}
          </section>`,
        )
        .join('')}`;
    }
  }

  /* ---------- NYC delivery form validation (DeliveryRequestForm.tsx) ---------- */

  const deliveryForm = document.getElementById('delivery-form');
  if (deliveryForm) {
    deliveryForm.addEventListener('submit', (e) => {
      let firstBad = null;
      $$('[data-rule]', deliveryForm).forEach((input) => {
        const v = input.value.trim();
        const rule = input.dataset.rule;
        const bad = (rule === 'required' && !v) || (rule === 'email' && !/^\S+@\S+\.\S+$/.test(v)) || (rule === 'phone' && v && v.replace(/\D/g, '').length < 10);
        const field = input.closest('.field');
        field.dataset.invalid = String(Boolean(bad));
        input.setAttribute('aria-invalid', String(Boolean(bad)));
        let err = $('.error', field);
        if (bad) {
          if (!err) {
            err = document.createElement('span');
            err.className = 'error';
            err.id = `${input.id}-err`;
            field.appendChild(err);
          }
          err.textContent = input.dataset.msg;
          input.setAttribute('aria-describedby', err.id);
          firstBad = firstBad || input;
        } else if (err) {
          err.remove();
          input.removeAttribute('aria-describedby');
        }
      });
      if (firstBad) {
        e.preventDefault();
        firstBad.focus();
      }
    });
  }

  /* ---------- scroll reveal (RevealObserver.tsx) ---------- */

  document.documentElement.dataset.hydrated = 'true';
  if (!reduced() && 'IntersectionObserver' in window) {
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            e.target.dataset.shown = 'true';
            io.unobserve(e.target);
          }
        }
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.08 },
    );
    const vh = window.innerHeight;
    $$('[data-reveal]:not([data-shown])').forEach((el) => {
      if (el.getBoundingClientRect().top < vh * 0.9) el.dataset.shown = 'true';
      else io.observe(el);
    });
    document.documentElement.classList.add('reveal-ready');
  }
})();
